// Shared building blocks for every Lead Desk screen and role. Styled only from the
// shell tokens (the same ones Reminders and Inbox use), so the eWings accent ramp
// reaches all of it. Nothing here knows about a role.
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Icon, type IconName } from 'src/front-components/lead-desk/icons';
import { ShellChip } from 'src/front-components/theme/page-chrome';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, SHELL_WEIGHT, v } from 'src/front-components/theme/shell-tokens';

export type Scheme = string;

// ------------------------------------------------------------------- avatar

export const Avatar = ({ text, size = 28 }: { text: string; size?: number }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      background: v('color-blue3'),
      color: v('color-blue10'),
      fontSize: size > 30 ? SHELL_TYPE.body.fontSize : SHELL_TYPE.caption.fontSize,
      fontWeight: SHELL_WEIGHT.semiBold,
    }}
  >
    {text}
  </span>
);

// --------------------------------------------------------------------- table

export type Column<T> = {
  key: string;
  header: ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T) => ReactNode;
};

export function DataTable<T>({
  scheme,
  columns,
  rows,
  rowKey,
  empty,
  dense = false,
}: {
  scheme: Scheme;
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string;
  empty: string;
  dense?: boolean;
}) {
  const shell = getShellColors(scheme);
  const template = columns.map((column) => column.width ?? '1fr').join(' ');
  const pad = dense ? SHELL_SPACE[1] : SHELL_SPACE[2];

  return (
    <div style={{ border: `1px solid ${shell.borderMedium}`, borderRadius: SHELL_RADIUS.md, overflow: 'hidden', background: shell.background, maxWidth: '100%', boxSizing: 'border-box' }}>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
        <div style={{ width: 'max-content', minWidth: '100%' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: template,
              gap: SHELL_SPACE[3],
              padding: `${SHELL_SPACE[2]} ${SHELL_SPACE[3]}`,
              background: shell.backgroundSecondary,
              borderBottom: `1px solid ${shell.borderMedium}`,
            }}
          >
        {columns.map((column) => (
          <div key={column.key} style={{ ...SHELL_TYPE.label, color: shell.textSecondary, textAlign: column.align ?? 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {column.header}
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div style={{ ...SHELL_TYPE.body, color: shell.textTertiary, textAlign: 'center', padding: SHELL_SPACE[8] }}>{empty}</div>
      ) : (
        rows.map((row) => (
          <div
            key={rowKey(row)}
            style={{
              display: 'grid',
              gridTemplateColumns: template,
              gap: SHELL_SPACE[3],
              alignItems: 'center',
              padding: `${pad} ${SHELL_SPACE[3]}`,
              borderBottom: `1px solid ${shell.borderLight}`,
              ...SHELL_TYPE.body,
              color: shell.text,
            }}
          >
            {columns.map((column) => (
              <div key={column.key} style={{ minWidth: 0, overflow: 'hidden', textAlign: column.align ?? 'left' }}>
                {column.render(row)}
              </div>
            ))}
          </div>
        ))
      )}
        </div>
      </div>
    </div>
  );
}

export const Pager = ({
  scheme,
  page,
  pages,
  onPage,
  summary,
}: {
  scheme: Scheme;
  page: number;
  pages: number;
  onPage: (page: number) => void;
  summary: string;
}) => {
  const shell = getShellColors(scheme);
  const button = (label: string, disabled: boolean, target: number): ReactNode => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPage(target)}
      style={{
        padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
        borderRadius: SHELL_RADIUS.sm,
        border: `1px solid ${shell.borderMedium}`,
        background: shell.background,
        color: shell.text,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer',
        ...SHELL_TYPE.body,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SHELL_SPACE[2], marginTop: SHELL_SPACE[3] }}>
      <span style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>{summary}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: SHELL_SPACE[2] }}>
        {button('Previous', page <= 1, page - 1)}
        <span style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>
          Page {page} of {pages}
        </span>
        {button('Next', page >= pages, page + 1)}
      </span>
    </div>
  );
};

// -------------------------------------------------------------- multi-select

/** Checkbox dropdown with Select all / Clear, like the desk's status and counsellor filters. */
export const MultiSelect = ({
  scheme,
  label,
  options,
  selected,
  onChange,
  locked,
  width = '160px',
}: {
  scheme: Scheme;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  /** Shown instead of the list, and not openable (a role fixed to one person). */
  locked?: string;
  width?: string;
}) => {
  const shell = getShellColors(scheme);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const close = (event: Event): void => {
      if (box.current !== null && event.target instanceof Node && !box.current.contains(event.target)) setOpen(false);
    };

    document.addEventListener('mousedown', close);

    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const trigger: CSSProperties = {
    width,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SHELL_SPACE[1],
    padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
    borderRadius: SHELL_RADIUS.sm,
    border: `1px solid ${shell.borderMedium}`,
    background: locked !== undefined ? shell.backgroundTertiary : shell.background,
    color: locked !== undefined ? shell.textTertiary : shell.text,
    cursor: locked !== undefined ? 'default' : 'pointer',
    ...SHELL_TYPE.body,
    boxSizing: 'border-box',
  };

  if (locked !== undefined) {
    return (
      <div style={trigger} title="Fixed for this role">
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{locked}</span>
      </div>
    );
  }

  return (
    <div ref={box} style={{ position: 'relative', width }}>
      <button type="button" onClick={() => setOpen(!open)} style={trigger}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected.length === 0 ? label : `${label.replace('Filter by ', '')} (${selected.length})`}
        </span>
        <span style={{ fontSize: SHELL_TYPE.caption.fontSize }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            top: '100%',
            left: 0,
            marginTop: SHELL_SPACE[1],
            minWidth: '100%',
            maxHeight: '260px',
            overflowY: 'auto',
            background: shell.background,
            border: `1px solid ${shell.borderMedium}`,
            borderRadius: SHELL_RADIUS.md,
            boxShadow: v('box-shadow-strong'),
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: `${SHELL_SPACE[2]} ${SHELL_SPACE[3]}`, ...SHELL_TYPE.body }}>
            <button type="button" onClick={() => onChange(options.map((o) => o.value))} style={linkButton}>
              Select all
            </button>
            <button type="button" onClick={() => onChange([])} style={linkButton}>
              Clear
            </button>
          </div>
          {options.length === 0 && (
            <div style={{ padding: SHELL_SPACE[3], ...SHELL_TYPE.caption, color: shell.textTertiary }}>Nothing to choose yet.</div>
          )}
          {options.map((option) => (
            <label
              key={option.value}
              style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[2], padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[3]}`, cursor: 'pointer', ...SHELL_TYPE.body, color: shell.text }}
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() =>
                  onChange(selected.includes(option.value) ? selected.filter((s) => s !== option.value) : [...selected, option.value])
                }
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const linkButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: v('color-blue'),
  fontSize: SHELL_TYPE.body.fontSize,
};

// --------------------------------------------------------------------- drawer

/** Right-hand panel over a dimmed page, closed by the X or by clicking outside. */
export const Drawer = ({
  scheme,
  title,
  subtitle,
  avatar,
  onClose,
  tabs,
  active,
  onTab,
  footer,
  children,
}: {
  scheme: Scheme;
  title: string;
  subtitle?: string;
  avatar?: string;
  onClose: () => void;
  tabs?: ReadonlyArray<{ id: string; label: string }>;
  active?: string;
  onTab?: (id: string) => void;
  footer?: ReactNode;
  children: ReactNode;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.35)' }} />
      <aside
        style={{
          position: 'relative',
          width: 'min(520px, 100vw)',
          height: '100%',
          background: shell.background,
          borderLeft: `1px solid ${shell.borderMedium}`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[3], padding: SHELL_SPACE[4], borderBottom: `1px solid ${shell.borderMedium}` }}>
          {avatar !== undefined && <Avatar text={avatar} size={36} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...SHELL_TYPE.sectionTitle, color: shell.text }}>{title}</div>
            {subtitle !== undefined && <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ ...linkButton, color: shell.textSecondary, fontSize: SHELL_TYPE.pageTitle.fontSize }}>
            ×
          </button>
        </header>

        {tabs !== undefined && onTab !== undefined && (
          <div style={{ display: 'flex', gap: SHELL_SPACE[4], padding: `0 ${SHELL_SPACE[4]}`, borderBottom: `1px solid ${shell.borderMedium}` }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: `${SHELL_SPACE[2]} 0`,
                  cursor: 'pointer',
                  ...SHELL_TYPE.body,
                  color: tab.id === active ? v('color-blue') : shell.textSecondary,
                  borderBottom: `2px solid ${tab.id === active ? v('color-blue') : 'transparent'}`,
                  marginBottom: '-1px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: SHELL_SPACE[4] }}>{children}</div>
        {footer !== undefined && (
          <footer style={{ display: 'flex', gap: SHELL_SPACE[2], padding: SHELL_SPACE[4], borderTop: `1px solid ${shell.borderMedium}` }}>{footer}</footer>
        )}
      </aside>
    </div>
  );
};

/** A read-only labelled value, as in the details panel. */
export const Fact = ({ scheme, label, value }: { scheme: Scheme; label: string; value: ReactNode }) => {
  const shell = getShellColors(scheme);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>{label}</div>
      <div
        style={{
          ...SHELL_TYPE.body,
          color: shell.text,
          marginTop: SHELL_SPACE.half,
          padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
          background: shell.backgroundSecondary,
          borderRadius: SHELL_RADIUS.sm,
          overflowWrap: 'anywhere',
        }}
      >
        {value === null || value === undefined || value === '' ? '—' : value}
      </div>
    </div>
  );
};

export const FactGrid = ({ children }: { children: ReactNode }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: SHELL_SPACE[3] }}>{children}</div>
);

export const GroupTitle = ({ scheme, children }: { scheme: Scheme; children: ReactNode }) => (
  <div style={{ ...SHELL_TYPE.label, color: getShellColors(scheme).textSecondary, margin: `${SHELL_SPACE[4]} 0 ${SHELL_SPACE[2]}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children}
  </div>
);

export const Notice = ({ tone, children }: { tone: 'red' | 'blue' | 'green' | 'orange'; children: ReactNode }) => (
  <div
    style={{
      padding: `${SHELL_SPACE[2]} ${SHELL_SPACE[3]}`,
      borderRadius: SHELL_RADIUS.md,
      background: v(`tag-background-${tone}`),
      color: v(`tag-text-${tone}`),
      ...SHELL_TYPE.bodyMedium,
      marginBottom: SHELL_SPACE[3],
    }}
  >
    {children}
  </div>
);

export const StatusChip = ({ label, color }: { label: string; color: string }) => <ShellChip label={label} color={color} />;

// -------------------------------------------------------------------- charts

const CHART_TEXT = { fontSize: 11 } as const;

/** Vertical bars with value labels. Pure SVG, so it draws inside the sandbox. */
export const BarChart = ({
  scheme,
  data,
  height = 180,
  onBarClick,
  activeKey,
}: {
  scheme: Scheme;
  data: ReadonlyArray<{ label: string; value: number; color?: string; key?: string }>;
  height?: number;
  /** Makes each bar a button: clicking reports the bar's key (or label). Used so a chart bar can filter the table below it. */
  onBarClick?: (key: string) => void;
  activeKey?: string;
}) => {
  const shell = getShellColors(scheme);
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = Math.max(320, data.length * 64);
  const plotH = height - 40;
  const slot = width / Math.max(1, data.length);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Bar chart" style={{ display: 'block' }}>
      <line x1={0} y1={plotH} x2={width} y2={plotH} stroke={shell.borderMedium} />
      {data.map((d, index) => {
        const h = (d.value / max) * (plotH - 16);
        const x = index * slot + slot * 0.2;

        const id = d.key ?? d.label;
        const clickable = onBarClick !== undefined;
        const dim = activeKey !== undefined && activeKey !== '' && activeKey !== id;

        return (
          <g
            key={id}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `${d.label}: ${d.value} students. Show them in the table.` : undefined}
            aria-pressed={clickable ? activeKey === id : undefined}
            onClick={clickable ? () => onBarClick(id) : undefined}
            onKeyDown={clickable ? (event) => { if (event.key === 'Enter' || event.key === ' ') onBarClick(id); } : undefined}
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: dim ? 0.35 : 1 }}
          >
            {clickable && <rect x={index * slot} y={0} width={slot} height={plotH + 20} fill="transparent" />}
            <rect x={x} y={plotH - h} width={slot * 0.6} height={Math.max(h, d.value > 0 ? 2 : 0)} rx={3} fill={d.color ?? v('color-blue')} />
            <text x={x + slot * 0.3} y={plotH - h - 4} textAnchor="middle" fill={shell.textSecondary} style={CHART_TEXT}>
              {d.value}
            </text>
            <text x={x + slot * 0.3} y={plotH + 14} textAnchor="middle" fill={shell.textTertiary} style={CHART_TEXT}>
              {d.label.length > 10 ? `${d.label.slice(0, 9)}…` : d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const DONUT_COLORS = ['blue', 'green', 'orange', 'purple', 'sky', 'pink', 'turquoise', 'yellow'] as const;

export const donutColor = (index: number): string => v(`color-${DONUT_COLORS[index % DONUT_COLORS.length]}`);

export const Donut = ({
  scheme,
  parts,
  centre,
  caption,
  onPartClick,
  activeKey,
}: {
  scheme: Scheme;
  parts: ReadonlyArray<{ label: string; value: number; key?: string }>;
  centre: string | number;
  caption: string;
  onPartClick?: (key: string) => void;
  activeKey?: string;
}) => {
  const shell = getShellColors(scheme);
  const total = Math.max(1, parts.reduce((sum, p) => sum + p.value, 0));
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg viewBox="0 0 140 140" width={140} height={140} role="img" aria-label="Donut chart" style={{ flexShrink: 0 }}>
      <circle cx={70} cy={70} r={radius} fill="none" stroke={shell.backgroundTertiary} strokeWidth={16} />
      {parts.map((part, index) => {
        const length = (part.value / total) * circumference;
        const partKey = part.key ?? part.label;
        const clickable = onPartClick !== undefined;
        const isDim = activeKey !== undefined && activeKey !== '' && activeKey !== partKey;
        const circle = (
          <circle
            key={part.label}
            cx={70}
            cy={70}
            r={radius}
            fill="none"
            stroke={donutColor(index)}
            strokeWidth={16}
            strokeDasharray={`${length} ${circumference - length}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 70 70)"
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `${part.label}: ${part.value} leads` : undefined}
            onClick={clickable ? () => onPartClick(partKey) : undefined}
            style={{
              cursor: clickable ? 'pointer' : 'default',
              opacity: isDim ? 0.35 : 1,
              transition: 'opacity 150ms ease, stroke-width 150ms ease',
            }}
          />
        );

        offset += length;

        return circle;
      })}
      <text x={70} y={64} textAnchor="middle" fill={shell.textTertiary} style={{ fontSize: 9, pointerEvents: 'none' }}>
        {caption}
      </text>
      <text x={70} y={86} textAnchor="middle" fill={shell.text} style={{ fontSize: 22, fontWeight: 600, pointerEvents: 'none' }}>
        {centre}
      </text>
    </svg>
  );
};

export const LineChart = ({ scheme, data, height = 170 }: { scheme: Scheme; data: ReadonlyArray<{ label: string; value: number }>; height?: number }) => {
  const shell = getShellColors(scheme);
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = 360;
  const plotH = height - 30;
  const step = width / Math.max(1, data.length - 1);
  const points = data.map((d, i) => `${i * step},${plotH - (d.value / max) * (plotH - 14) - 4}`);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Line chart" style={{ display: 'block' }}>
      <line x1={0} y1={plotH} x2={width} y2={plotH} stroke={shell.borderMedium} />
      <polyline points={points.join(' ')} fill="none" stroke={v('color-blue')} strokeWidth={2} strokeLinejoin="round" />
      {data.map((d, i) => {
        const [x, y] = points[i].split(',').map(Number);

        return (
          <g key={d.label}>
            <circle cx={x} cy={y} r={3.5} fill={shell.background} stroke={v('color-blue')} strokeWidth={2} />
            <text x={x} y={plotH + 14} textAnchor="middle" fill={shell.textTertiary} style={CHART_TEXT}>
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/** Horizontal progress rows, as in the funnel and lead-source cards. */
export const MeterRow = ({ scheme, label, count, share, color }: { scheme: Scheme; label: string; count: number; share: number; color?: string }) => {
  const shell = getShellColors(scheme);

  return (
    <div style={{ marginBottom: SHELL_SPACE[3] }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', ...SHELL_TYPE.body, color: shell.text }}>
        <span>{label}</span>
        <span style={{ color: shell.textSecondary }}>
          {count} ({share}%)
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: shell.backgroundTertiary, marginTop: SHELL_SPACE[1], overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, Math.max(count > 0 ? 2 : 0, share))}%`, height: '100%', background: color ?? v('color-blue') }} />
      </div>
    </div>
  );
};

/** A filter row: a wrapping flex line that keeps its children aligned. */
export const FilterBar = ({ scheme, children }: { scheme: Scheme; children: ReactNode }) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: SHELL_SPACE[2],
        padding: SHELL_SPACE[3],
        marginBottom: SHELL_SPACE[3],
        background: shell.backgroundSecondary,
        border: `1px solid ${shell.borderMedium}`,
        borderRadius: SHELL_RADIUS.md,
      }}
    >
      {children}
    </div>
  );
};


// ------------------------------------------------------------- row actions

/** A square icon button for table rows (Edit, Block, Delete, Details). `tone` colours it like the desk's green/red actions. */
export const IconButton = ({
  scheme,
  icon,
  label,
  onClick,
  tone = 'neutral',
  disabled = false,
}: {
  scheme: Scheme;
  icon: IconName;
  label: string;
  onClick: () => void;
  tone?: 'neutral' | 'green' | 'red' | 'blue';
  disabled?: boolean;
}) => {
  const shell = getShellColors(scheme);
  const colour = tone === 'neutral' ? shell.textSecondary : v(`tag-text-${tone}`);
  const fill = tone === 'neutral' ? shell.background : v(`tag-background-${tone}`);

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: SHELL_RADIUS.sm,
        border: `1px solid ${tone === 'neutral' ? shell.borderMedium : 'transparent'}`,
        background: fill,
        color: colour,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Icon name={icon} size={14} />
    </button>
  );
};

/**
 * A compact dropdown that saves as soon as it changes (the Counselor / Status / Telecaller cells). It shows the new
 * value at once and puts the old one back, with the message, if the save fails.
 */
export const InlineSelect = ({
  scheme,
  value,
  options,
  onSave,
  onError,
  width = '140px',
  label,
  disabled = false,
}: {
  scheme: Scheme;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onSave: (next: string) => Promise<void>;
  onError: (message: string) => void;
  width?: string;
  label: string;
  disabled?: boolean;
}) => {
  const shell = getShellColors(scheme);
  const [pending, setPending] = useState<string | null>(null);
  const shown = pending ?? value;
  const known = options.some((o) => o.value === shown);

  return (
    <select
      aria-label={label}
      value={shown}
      disabled={disabled || pending !== null}
      onChange={(event) => {
        const next = event.target.value;

        setPending(next);
        void onSave(next)
          .catch((cause: unknown) => onError(cause instanceof Error ? cause.message : 'Could not save that change.'))
          .finally(() => setPending(null));
      }}
      style={{
        width,
        maxWidth: '100%',
        padding: `${SHELL_SPACE.half} ${SHELL_SPACE[1]}`,
        borderRadius: SHELL_RADIUS.sm,
        border: `1px solid ${shell.borderMedium}`,
        background: shell.background,
        color: shell.text,
        ...SHELL_TYPE.body,
        opacity: pending !== null ? 0.6 : 1,
      }}
    >
      {!known && <option value={shown}>{shown === '' ? '—' : shown}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

/** A button that opens a small menu of actions, closed by choosing one, pressing Escape or clicking away. */
export const MenuButton = ({
  scheme,
  label,
  icon,
  items,
  disabled = false,
}: {
  scheme: Scheme;
  label: string;
  icon?: IconName;
  items: ReadonlyArray<{ id: string; label: string; onSelect: () => void; disabled?: boolean }>;
  disabled?: boolean;
}) => {
  const shell = getShellColors(scheme);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const close = (event: Event): void => {
      if (box.current !== null && event.target instanceof Node && !box.current.contains(event.target)) setOpen(false);
    };
    const esc = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);

    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={box} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: SHELL_SPACE[1],
          padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
          borderRadius: SHELL_RADIUS.sm,
          border: `1px solid ${shell.borderMedium}`,
          background: shell.background,
          color: shell.text,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          ...SHELL_TYPE.body,
        }}
      >
        {icon !== undefined && <Icon name={icon} size={14} />}
        {label}
        <Icon name="chevronDown" size={12} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: SHELL_SPACE[1],
            zIndex: 30,
            minWidth: 220,
            background: shell.background,
            border: `1px solid ${shell.borderMedium}`,
            borderRadius: SHELL_RADIUS.md,
            boxShadow: v('box-shadow-strong'),
            padding: SHELL_SPACE.half,
          }}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: `${SHELL_SPACE[1.5]} ${SHELL_SPACE[2]}`,
                border: 'none',
                borderRadius: SHELL_RADIUS.sm,
                background: 'transparent',
                color: shell.text,
                opacity: item.disabled === true ? 0.45 : 1,
                cursor: item.disabled === true ? 'default' : 'pointer',
                ...SHELL_TYPE.body,
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** A centred yes / no dialog for actions that cannot be undone. Escape or clicking outside cancels. */
export const ConfirmDialog = ({
  scheme,
  title,
  message,
  confirmLabel,
  busy = false,
  error,
  onConfirm,
  onCancel,
}: {
  scheme: Scheme;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const shell = getShellColors(scheme);

  useEffect(() => {
    const esc = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !busy) onCancel();
    };

    document.addEventListener('keydown', esc);

    return () => document.removeEventListener('keydown', esc);
  }, [busy, onCancel]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={busy ? undefined : onCancel} style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.45)' }} />
      <div
        role="alertdialog"
        aria-label={title}
        style={{ position: 'relative', width: 'min(420px, 92vw)', background: shell.background, border: `1px solid ${shell.borderMedium}`, borderRadius: SHELL_RADIUS.md, padding: SHELL_SPACE[4], boxShadow: v('box-shadow-strong') }}
      >
        <div style={{ ...SHELL_TYPE.sectionTitle, color: shell.text, marginBottom: SHELL_SPACE[2] }}>{title}</div>
        <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary, marginBottom: SHELL_SPACE[3] }}>{message}</div>
        {error !== undefined && error !== null && <div style={{ ...SHELL_TYPE.caption, color: v('color-red'), marginBottom: SHELL_SPACE[2] }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SHELL_SPACE[2], flexWrap: 'wrap' }}>
          <button type="button" disabled={busy} onClick={onCancel} style={{ padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[3]}`, borderRadius: SHELL_RADIUS.sm, border: `1px solid ${shell.borderMedium}`, background: shell.background, color: shell.text, cursor: 'pointer', ...SHELL_TYPE.body }}>
            Cancel
          </button>
          <button type="button" disabled={busy} onClick={onConfirm} style={{ padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[3]}`, borderRadius: SHELL_RADIUS.sm, border: 'none', background: v('color-red'), color: '#fff', cursor: 'pointer', ...SHELL_TYPE.bodyMedium }}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/** The small pill shown beside a record that was changed after it was created. */
export const UpdatedBadge = () => null;
